<template>
  <div class="hero-wrapper" @mousemove="handleMouseMove" ref="heroWrapper">
    <div class="hero-content">
      <div class="animated-lines">
        <div class="line line-1"></div>
        <div class="line line-2"></div>
        <div class="line line-3"></div>
        <div class="line line-4"></div>
      </div>

      <div class="floating-elements">
        <div
          class="float-circle circle-1"
          :style="getParallaxStyle(0.06)"
        ></div>
        <div
          class="float-circle circle-2"
          :style="getParallaxStyle(0.04)"
        ></div>
        <div class="float-ring ring-1" :style="getParallaxStyle(0.05)"></div>
        <div class="float-ring ring-2" :style="getParallaxStyle(0.03)"></div>
      </div>

      <v-container class="content-grid" fluid>
        <v-row align="center" justify="center">
          <v-col cols="12" md="6" class="main-content">
            <div class="content-wrapper" :style="getParallaxStyle(0.02)">
              <div class="badge">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle cx="12" cy="12" r="10" stroke-width="2" />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 6v6l4 2"
                  />
                </svg>
                <span>Save Time, Live Better</span>
              </div>

              <h1 class="hero-title">
                Your everyday helper<br />
                right in
                <span class="highlight">your pocket</span>
              </h1>

              <p class="hero-description">
                Experience seamless convenience with our powerful mobile app.
                Get things done faster, smarter, and easier than ever before.
              </p>

              <div class="cta-section">
                <div class="store-buttons">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.ohyes.app&pcampaignid=web_share"
                    target="_blank"
                    class="store-button primary"
                  >
                    <svg
                      class="store-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M17.9 17.39c-.26-.8-1.01-1.39-1.9-1.39h-1v-3a1 1 0 0 0-1-1H8v-2h2a1 1 0 0 0 1-1V7h2a2 2 0 0 0 2-2v-.41c2.93 1.18 5 4.05 5 7.41 0 2.08-.8 3.97-2.1 5.39M11 19.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.22.21-1.79L9 15v1a2 2 0 0 0 2 2m1-16A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2z"
                      />
                    </svg>
                    <div class="store-text">
                      <span class="store-label">GET IT ON</span>
                      <span class="store-name">Google Play</span>
                    </div>
                  </a>

                  <a
                    href="https://apps.apple.com/in/app/oh-yes-home-solution/id6471665769"
                    target="_blank"
                    class="store-button"
                  >
                    <svg
                      class="store-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
                      />
                    </svg>
                    <div class="store-text">
                      <span class="store-label">Download on the</span>
                      <span class="store-name">App Store</span>
                    </div>
                  </a>
                </div>

                <div class="stats">
                  <div class="stat-item">
                    <div class="stat-number">75K+</div>
                    <div class="stat-label">Active Users</div>
                  </div>
                  <div class="stat-divider"></div>
                  <div class="stat-item">
                    <div class="stat-number">4.8</div>
                    <div class="stat-label">App Rating</div>
                  </div>
                  <div class="stat-divider"></div>
                  <div class="stat-item">
                    <div class="stat-number">100%</div>
                    <div class="stat-label">Free to Use</div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="6" class="phone-section">
            <div
              style="padding-top: 30px"
              class="phone-container"
              :style="getParallaxStyle(0.03)"
            >
              <div class="phone-mockup">
                <div class="phone-screen">
                  <div class="carousel">
                    <transition name="slide">
                      <img
                        :key="currentImageIndex"
                        :src="currentImage"
                        alt="OH YES App"
                        class="phone-image"
                      />
                    </transition>
                  </div>

                  <div class="carousel-indicators">
                    <span
                      v-for="(img, index) in currentImages"
                      :key="index"
                      :class="[
                        'indicator',
                        { active: currentImageIndex === index },
                      ]"
                      @click="goToSlide(index)"
                    ></span>
                  </div>
                </div>
              </div>

              <!-- Decorative Elements around phone -->
              <div class="phone-glow"></div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- Features Highlight Section -->
      <div class="features-highlight">
        <div class="features-container">
          <div class="feature-card">
            <div class="feature-icon-wrapper">
              <svg
                class="feature-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div class="feature-content">
              <h3>Lightning Fast</h3>
              <p>Book in 60 seconds</p>
            </div>
          </div>

          <div class="feature-card">
            <div class="feature-icon-wrapper">
              <svg
                class="feature-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <div class="feature-content">
              <h3>Verified Experts</h3>
              <p>Trusted professionals</p>
            </div>
          </div>

          <div class="feature-card">
            <div class="feature-icon-wrapper">
              <svg
                class="feature-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div class="feature-content">
              <h3>Best Prices</h3>
              <p>Transparent pricing</p>
            </div>
          </div>

          <div class="feature-card">
            <div class="feature-icon-wrapper">
              <svg
                class="feature-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div class="feature-content">
              <h3>24/7 Support</h3>
              <p>Always available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HeroSection",
  data() {
    return {
      currentImageIndex: 0,
      imagesIndia: [
        require("@/assets/images/phoneimage.jpeg"),
        require("@/assets/images/phoneimage2.jpeg"),
        require("@/assets/images/phoneimage3.jpeg"),
        require("@/assets/images/phoneimage4.jpeg"),
      ],
      imagesUS: [
        require("@/assets/images/phoneimageus.jpeg"),
        require("@/assets/images/phoneimageus2.jpeg"),
        require("@/assets/images/phoneimageus3.jpeg"),
      ],
      autoPlayInterval: null,
      mouseX: 0,
      mouseY: 0,
      scrollY: 0,
    };
  },
  computed: {
    currentLocation() {
      // Read location from route meta, same as testimonials and footer
      return (
        (this.$route && this.$route.meta && this.$route.meta.location) ||
        "india" // default to India
      );
    },
    currentImages() {
      // Return US images if location is "us", otherwise India images
      if (this.currentLocation === "us") {
        return this.imagesUS;
      }
      return this.imagesIndia;
    },
    currentImage() {
      return this.currentImages[this.currentImageIndex];
    },
  },
  watch: {
    // When location changes (route changes), reset the carousel
    currentLocation() {
      this.currentImageIndex = 0;
      this.stopAutoPlay();
      this.startAutoPlay();
    },
  },
  mounted() {
    this.startAutoPlay();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    this.stopAutoPlay();
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleMouseMove(e) {
      const rect = this.$refs.heroWrapper.getBoundingClientRect();
      this.mouseX = (e.clientX - rect.left - rect.width / 2) / rect.width;
      this.mouseY = (e.clientY - rect.top - rect.height / 2) / rect.height;
    },
    handleScroll() {
      this.scrollY = window.pageYOffset;
    },
    getParallaxStyle(intensity) {
      const moveX = this.mouseX * intensity * 50;
      const moveY =
        this.mouseY * intensity * 50 + this.scrollY * intensity * 0.5;

      return {
        transform: `translate(${moveX}px, ${moveY}px)`,
        transition: "transform 0.3s ease-out",
      };
    },
    startAutoPlay() {
      this.autoPlayInterval = setInterval(() => {
        this.nextSlide();
      }, 3000);
    },
    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
      }
    },
    nextSlide() {
      this.currentImageIndex =
        (this.currentImageIndex + 1) % this.currentImages.length;
    },
    goToSlide(index) {
      this.currentImageIndex = index;
      this.stopAutoPlay();
      this.startAutoPlay();
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap");

/*
|--------------------------------------------------------------------------
| FEATURES HIGHLIGHT SECTION
|--------------------------------------------------------------------------
*/

.features-highlight {
  width: 100%;
  background: white;
  padding: 30px 0 40px;
  position: relative;
  z-index: 100;
}

.features-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding: 0 60px;
}

@media (min-width: 1600px) {
  .features-container {
    max-width: 2000px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    padding: 0 60px;
  }
}

.feature-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #fff 0%, #fafafa 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  border: 1px solid #f5f5f5;
}

.feature-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(255, 107, 53, 0.1);
  border-color: rgba(255, 107, 53, 0.2);
}

.feature-icon-wrapper {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #fff5f0, #ffe8df);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon {
  width: 18px;
  height: 18px;
  stroke: #ff6b35;
  stroke-width: 2;
}

.feature-content h3 {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 2px 0;
  font-family: "DM Sans", sans-serif;
}

.feature-content p {
  font-size: 11px;
  color: #666;
  margin: 0;
  line-height: 1.3;
}

/*
|--------------------------------------------------------------------------
| BASE STYLES
|--------------------------------------------------------------------------
*/

.hero-wrapper {
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.hero-content {
  position: relative;
  width: 100%;
  flex: 1;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  justify-content: start;
}

.content-grid {
  z-index: 10;
  position: relative;
  max-width: 1400px;
}
@media (min-width: 1600px) {
  .content-grid {
    z-index: 10;
    position: relative;
    max-width: 2000px;
  }
}

/*
|--------------------------------------------------------------------------
| BACKGROUND EFFECTS
|--------------------------------------------------------------------------
*/

.animated-lines {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
}

.line {
  position: absolute;
  width: 2px;
  height: 150%;
  background: rgba(255, 107, 53, 0.1);
  transform: rotate(-45deg);
  animation: line-move 12s linear infinite;
}

.line-1 {
  left: 10%;
  top: -50%;
  animation-delay: 0s;
}

.line-2 {
  left: 30%;
  top: -60%;
  animation-delay: 3s;
  background: rgba(74, 144, 226, 0.1);
}

.line-3 {
  left: 55%;
  top: -40%;
  animation-delay: 6s;
}

.line-4 {
  left: 75%;
  top: -70%;
  animation-delay: 9s;
  background: rgba(74, 144, 226, 0.1);
}

@keyframes line-move {
  0% {
    transform: translateY(-50%) rotate(-45deg);
  }
  100% {
    transform: translateY(100%) rotate(-45deg);
  }
}

.floating-elements {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}

.float-circle {
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  opacity: 0.1;
  animation: float-up 6s ease-in-out infinite;
}

.circle-1 {
  top: 20%;
  left: 10%;
}

.circle-2 {
  bottom: 30%;
  right: 15%;
  animation-delay: 3s;
}

.float-ring {
  position: absolute;
  width: 80px;
  height: 80px;
  border: 3px solid #ff6b35;
  border-radius: 50%;
  opacity: 0.15;
  animation: float-rotate 8s ease-in-out infinite;
}

.ring-1 {
  top: 40%;
  right: 8%;
}

.ring-2 {
  bottom: 20%;
  left: 12%;
  animation-delay: 4s;
}

@keyframes float-up {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-20px) scale(1.1);
  }
}

@keyframes float-rotate {
  0%,
  100% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.1);
  }
}

/*
|--------------------------------------------------------------------------
| MAIN CONTENT
|--------------------------------------------------------------------------
*/

.main-content {
  padding-left: 60px;
  z-index: 10;
}

.content-wrapper {
  max-width: 600px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #fff, #ffe8df);
  border: 1px solid rgba(255, 107, 53, 0.2);
  border-radius: 50px;
  font-size: 13px;
  font-weight: 600;
  color: #ff6b35;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.1);
  animation: badge-pulse 2s ease-in-out infinite;
}

@keyframes badge-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

.hero-title {
  font-size: 46px;
  font-family: "DM Sans", sans-serif;
  font-weight: 800;
  line-height: 1.1;
  color: #1a1a1a;
  margin-bottom: 20px;
  letter-spacing: -2px;
}

.highlight {
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
}

.hero-description {
  font-size: 17px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 32px;
  font-weight: 400;
}

.cta-section {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.store-buttons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.store-button {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  background: #ffffff;
  border: 2px solid #e8e8e8;
  color: #1a1a1a;
  border-radius: 16px;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.store-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.store-button.primary {
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  border: none;
  color: white;
  box-shadow: 0 8px 24px rgba(255, 107, 53, 0.25);
}

.store-button.primary:hover {
  box-shadow: 0 12px 32px rgba(255, 107, 53, 0.35);
}

.store-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.store-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.store-label {
  font-size: 10px;
  font-weight: 600;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.store-name {
  font-size: 16px;
  font-weight: 700;
}

.stats {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px 0;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-number {
  font-size: 28px;
  font-weight: 800;
  color: #ff6b35;
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: linear-gradient(to bottom, transparent, #ddd, transparent);
}

/*
|--------------------------------------------------------------------------
| PHONE SECTION (iPhone Style)
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| PHONE SECTION (iPhone Style)
|--------------------------------------------------------------------------
*/

.phone-section {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.phone-container {
  position: relative;
  will-change: transform;
  padding: 20px;
}

.phone-mockup {
  width: 280px;
  height: 560px;
  background: linear-gradient(145deg, #e8e9ed, #f5f6f8);
  border-radius: 50px;
  padding: 12px;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  position: relative;
  z-index: 2;
  border: 1px solid #d1d2d6;
}

/* Dynamic Island - moved lower and refined */
.phone-mockup::before {
  content: "";
  position: absolute;
  top: 22px;
  left: 50%;
  transform: translateX(-50%);
  width: 110px;
  height: 32px;
  background: #000000;
  border-radius: 20px;
  z-index: 3;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.8),
    0 0 0 0.5px rgba(255, 255, 255, 0.1);
}

/* Side buttons - power button */
.phone-mockup::after {
  content: "";
  position: absolute;
  right: -3px;
  top: 140px;
  width: 4px;
  height: 60px;
  background: linear-gradient(to right, #d1d2d6, #e8e9ed);
  border-radius: 0 3px 3px 0;
  box-shadow: inset -1px 0 2px rgba(0, 0, 0, 0.1),
    1px 0 1px rgba(255, 255, 255, 0.8);
}

.phone-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  height: 400px;
  background: radial-gradient(
    circle,
    rgba(255, 107, 53, 0.15) 0%,
    transparent 70%
  );
  pointer-events: none;
  z-index: 1;
  animation: glow-pulse 4s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.1);
  }
}

.phone-screen {
  width: 100%;
  height: 100%;
  background: #000;
  border-radius: 40px;
  overflow: hidden;
  position: relative;
}

.carousel {
  width: 100%;
  height: 100%;
  position: relative;
}

.phone-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.6s ease;
}

.slide-enter {
  transform: translateX(100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.carousel-indicators {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.indicator {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator:hover {
  background: rgba(255, 255, 255, 0.6);
}

.indicator.active {
  background: #ff6b35;
  width: 24px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.6);
}

/*
|--------------------------------------------------------------------------
| RESPONSIVE
|--------------------------------------------------------------------------
*/

@media (max-width: 960px) {
  .features-highlight {
    padding: 20px 0 30px;
  }

  .features-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 0 20px;
  }

  .feature-card {
    padding: 12px;
    flex-direction: column;
    text-align: center;
  }

  .feature-icon-wrapper {
    width: 32px;
    height: 32px;
  }

  .feature-icon {
    width: 16px;
    height: 16px;
  }

  .feature-content h3 {
    font-size: 12px;
  }

  .feature-content p {
    font-size: 10px;
  }

  .hero-title {
    font-size: 36px;
    letter-spacing: -1px;
  }

  .hero-description {
    font-size: 16px;
  }

  .store-buttons {
    flex-direction: column;
    width: 100%;
    max-width: 320px;
  }

  .store-button {
    width: 100%;
  }

  .stats {
    flex-wrap: wrap;
    gap: 16px;
  }

  .stat-divider {
    display: none;
  }

  .phone-mockup {
    width: 240px;
    height: 480px;
    border-radius: 35px;
    padding: 10px;
  }

  .phone-screen {
    border-radius: 25px;
  }

  .phone-glow {
    width: 300px;
    height: 300px;
  }
}
</style>